import { useEffect, useMemo, useRef, useState } from "react";

const ALERT_READ_KEYS_STORAGE_KEY = "monity.alert.read.keys.v1";
const ALERT_READ_CUTOFF_STORAGE_KEY = "monity.alert.read.cutoff.v1";
const ALERT_READ_KEYS_MAX = 500;

const DEFAULT_POLL_MS = 3000;      // 종 알림 갱신 주기(활성 탭) 8초→3초
const OPEN_PANEL_POLL_MS = 2000;   // 종 패널 열어놨을 때 5초→2초
const HIDDEN_TAB_POLL_MS = 60000;  // 백그라운드 탭(브라우저가 어차피 제한) — 유지

function loadReadAlertKeys() {
  try {
    const raw = localStorage.getItem(ALERT_READ_KEYS_STORAGE_KEY);
    const parsed = JSON.parse(raw || "[]");
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter(Boolean));
  } catch {
    return new Set();
  }
}

function loadReadAlertCutoff() {
  try {
    const raw = localStorage.getItem(ALERT_READ_CUTOFF_STORAGE_KEY);
    const n = Number(raw || 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function saveReadAlertState(readKeysSet, cutoffMs) {
  try {
    const keys = Array.from(readKeysSet || []).filter(Boolean);
    const sliced =
      keys.length > ALERT_READ_KEYS_MAX
        ? keys.slice(keys.length - ALERT_READ_KEYS_MAX)
        : keys;

    localStorage.setItem(ALERT_READ_KEYS_STORAGE_KEY, JSON.stringify(sliced));
    localStorage.setItem(
      ALERT_READ_CUTOFF_STORAGE_KEY,
      String(Number(cutoffMs || 0))
    );
  } catch {}
}

// (여기 있던 isAdminUser 는 isServerScopedUser 로 대체돼 2026-10-07 제거)

// 서버가 이벤트 범위를 이미 걸러 주는 계정들(App.js 의 adminLike 와 같은 기준).
//   본사·지점 관리자가 여기 빠져 있어서, 이들 계정의 종에는 카메라 켜짐/꺼짐만 뜨고
//   정작 «안전장구 미착용»이 안 떴다(2026-10-07 수정).
function isServerScopedUser(user) {
  const role = String(user?.roleCode || "").trim().toUpperCase();

  return (
    role === "SUPER_ADMIN" ||
    role === "ADMIN" ||
    role === "HQ_MANAGER" ||
    role === "BRANCH_MANAGER"
  );
}

function normalizeKey(value) {
  return String(value || "").trim();
}

function stripLivePrefix(value) {
  return normalizeKey(value).replace(/^\/?live\//i, "");
}

function buildCameraScopeSet(cameras) {
  const set = new Set();

  (Array.isArray(cameras) ? cameras : []).forEach((cam, idx) => {
    const streamKey = normalizeKey(cam?.streamKey);
    const camIdx = normalizeKey(cam?.camIdx || cam?.id);
    const camNo = normalizeKey(cam?.camNo);
    const deviceId = normalizeKey(cam?.deviceId);
    const path = normalizeKey(cam?.path);
    const streamPath = normalizeKey(cam?.streamPath);
    const name = normalizeKey(cam?.name);
    const camName = normalizeKey(cam?.camName);
    const camTitle = normalizeKey(cam?.camTitle);
    const title = normalizeKey(cam?.title);
    const fallbackName = camNo ? `CAM ${camNo}` : `CAM ${idx + 1}`;

    const candidates = [
      streamKey,
      streamKey ? `live/${streamKey}` : "",
      streamKey ? `/live/${streamKey}` : "",
      camIdx,
      camNo,
      deviceId,
      path,
      streamPath,
      name,
      camName,
      camTitle,
      title,
      fallbackName,
    ].filter(Boolean);

    candidates.forEach((key) => {
      set.add(key);
      set.add(stripLivePrefix(key));
    });
  });

  return set;
}

function normalizeEventType(log) {
  const eventType = String(log?.eventType || "").trim().toUpperCase();
  if (eventType) return eventType;

  const type = String(log?.type || "").trim();

  if (type.includes("SOS 해제")) return "SOS_DISMISS";
  if (type.includes("심박수 이상 해제")) return "HEART_RATE_HIGH_DISMISS";
  if (type.includes("심박수 이상")) return "HEART_RATE_HIGH";
  if (type === "SOS") return "SOS";
  if (type.includes("위험 감지")) return "SOS";
  if (type.includes("민트캠 켜짐")) return "MINTCAM_ON";
  if (type.includes("민트캠 꺼짐")) return "MINTCAM_OFF";

  return type.toUpperCase();
}

// (여기 있던 isMintcamEvent 는 «카메라 켜짐/꺼짐류만 종에 올리는» 필터였다.
//  그게 안전장구 미착용을 떨어뜨린 원인이라 2026-10-07 제거했다.
//  지금은 내 카메라에서 난 일이면 종류를 가리지 않는다.)

function belongsToMyCamera(log, cameraScopeSet) {
  if (!cameraScopeSet || cameraScopeSet.size === 0) return false;

  const candidates = [
    log?.deviceName,
    log?.deviceId,
    log?.streamKey,
    log?.streamPath,
    log?.path,
    log?.cameraId,
    log?.camIdx,
    log?.camNo,
    log?.name,
    log?.camName,
    log?.camTitle,
  ]
    .map(normalizeKey)
    .filter(Boolean);

  for (const key of candidates) {
    if (cameraScopeSet.has(key)) return true;

    const stripped = stripLivePrefix(key);
    if (stripped && cameraScopeSet.has(stripped)) return true;
  }

  return false;
}

function filterLogsByAccountScope(logs, currentUser, scopedCameras) {
  const rows = Array.isArray(logs) ? logs : [];

  // 서버(/api/event-log)가 이미 계정 범위(getScopedDeviceIds)로 걸러 보낸다.
  //   관리자·본사·지점 계정은 그 결과를 그대로 쓴다. 여기서 또 거르면
  //   서버가 보여주기로 한 것을 화면이 임의로 버리게 된다.
  if (isServerScopedUser(currentUser)) {
    return rows;
  }

  const cameraScopeSet = buildCameraScopeSet(scopedCameras);

  if (cameraScopeSet.size === 0) {
    return [];
  }

  // 내 카메라에서 난 일이면 종류를 가리지 않는다.
  //   예전에는 isMintcamEvent(카메라 켜짐/꺼짐류)만 통과시켜서,
  //   «안전장구 미착용»이 종에서 통째로 빠지고 켜짐/꺼짐만 울렸다(2026-10-07 수정).
  return rows.filter((log) => belongsToMyCamera(log, cameraScopeSet));
}

// 종(🔔)과 토스트에 띄울 것 — «사람이 당장 조치해야 하는 것»만.
//   2026-10-07: 카메라 켜짐/꺼짐(MINTCAM_ON/OFF)을 뺐다.
//   앱을 끌 때마다 종이 울려 알림이 너무 많아졌고, 촬영 중단은 조치 대상이 아니라 상태 변화다.
//   (장비 화면·이벤트 목록에는 그대로 남으니 기록이 사라지는 건 아니다.)
function isBellTarget(log) {
  const eventType = normalizeEventType(log);

  if (
    eventType === "SOS_DISMISS" ||
    eventType === "HEART_RATE_HIGH_DISMISS"
  ) {
    return false;
  }

  if (
    eventType === "PPE_VIOLATION" || // 안전장구 미착용 — 종 알림의 본래 목적
    eventType === "SOS" || // 긴급호출 — 생명 관련이라 유지
    eventType === "HEART_RATE_HIGH" // 심박 이상 — 생명 관련이라 유지
  ) {
    return true;
  }

  return false;
}

function formatDisplayTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("ko-KR", { timeZone: "Asia/Seoul" });
}

function toEventMs(value) {
  if (!value) return 0;
  const d = new Date(value).getTime();
  return Number.isFinite(d) ? d : 0;
}

function makeKey(log) {
  return `${log?.timestamp || ""}__${log?.eventType || log?.type || ""}__${
    log?.deviceId || ""
  }__${log?.message || ""}`;
}

function enrichLog(log) {
  const timestamp =
    log?.timestamp ||
    log?.createdAt ||
    log?.updatedAt ||
    log?.eventTime ||
    null;

  const eventType = normalizeEventType(log);

  return {
    ...log,
    timestamp,
    eventType,
    displayTime: log?.displayTime || formatDisplayTime(timestamp),
    _key: log?._key || makeKey({ ...log, timestamp, eventType }),
    _ms: toEventMs(timestamp),
  };
}

export default function useAlertCenter(apiBase = "", options = {}) {
  const {
    enabled = true,
    allowBell = true,
    allowEventLogs = true,
    includeDangerSummary = false,
    currentUser = null,
    scopedCameras = [],
  } = options;

  const scopedCameraKey = useMemo(() => {
    return JSON.stringify(
      (Array.isArray(scopedCameras) ? scopedCameras : []).map((cam) => ({
        id: cam?.id ?? cam?.camIdx ?? "",
        camIdx: cam?.camIdx ?? "",
        camNo: cam?.camNo ?? "",
        streamKey: cam?.streamKey ?? "",
        deviceId: cam?.deviceId ?? "",
        path: cam?.path ?? "",
        streamPath: cam?.streamPath ?? "",
        name: cam?.name ?? cam?.camName ?? cam?.camTitle ?? "",
      }))
    );
  }, [scopedCameras]);

  const [alertOpen, setAlertOpen] = useState(false);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [toastItem, setToastItem] = useState(null);
  const [recentAlertLogs, setRecentAlertLogs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [todayEventCount, setTodayEventCount] = useState(0);
  const [activeDangerLogs, setActiveDangerLogs] = useState([]);

  const seenAlertKeysRef = useRef(new Set());
  const readAlertKeysRef = useRef(loadReadAlertKeys());
  const readAlertCutoffRef = useRef(loadReadAlertCutoff());
  const eventLogBootstrappedRef = useRef(false);
  const pollTimerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const fetchInFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }

      setAlertOpen(false);
      setUnreadAlerts(0);
      setToastItem(null);
      setRecentAlertLogs([]);
      setLogs([]);
      setActiveDangerLogs([]);
      fetchInFlightRef.current = false;
      return;
    }

    let cancelled = false;

    const isReadAlert = (item) => {
      if (!item?._key) return false;

      if (readAlertKeysRef.current.has(item._key)) {
        return true;
      }

      if (
        item?._ms > 0 &&
        readAlertCutoffRef.current > 0 &&
        item._ms <= readAlertCutoffRef.current
      ) {
        return true;
      }

      return false;
    };

    const getNextPollMs = () => {
      if (typeof document !== "undefined" && document.hidden) {
        return HIDDEN_TAB_POLL_MS;
      }
      return alertOpen ? OPEN_PANEL_POLL_MS : DEFAULT_POLL_MS;
    };

    const clearPollTimer = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const scheduleNextFetch = (delayMs) => {
      if (cancelled) return;
      clearPollTimer();
      pollTimerRef.current = setTimeout(() => {
        fetchLogs();
      }, delayMs);
    };

    const fetchLogs = async () => {
      if (cancelled || fetchInFlightRef.current) return;

      fetchInFlightRef.current = true;

      try {
        // 서버에 저장된 계정별 '읽음 기준시각'을 병렬로 가져온다(다기기 동기화).
        const readStatePromise = fetch(`${apiBase}/api/alert-read-state`, {
          cache: "no-store",
          credentials: "include",
        })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);

        const requests = [
          fetch(`${apiBase}/api/event-log`, {
            cache: "no-store",
            credentials: "include",
          }),
        ];

        if (includeDangerSummary) {
          requests.push(
            fetch(`${apiBase}/api/sos-summary`, {
              cache: "no-store",
              credentials: "include",
            })
          );
        }

        const responses = await Promise.all(requests);
        const eventLogRes = responses[0];
        const eventLogData = await eventLogRes.json();

        const rawEventRows = Array.isArray(eventLogData) ? eventLogData : [];
        const enrichedEventLogs = rawEventRows.map(enrichLog);

        const scopedEventLogs = filterLogsByAccountScope(
          enrichedEventLogs,
          currentUser,
          scopedCameras
        );

        if (allowEventLogs) {
          setLogs(scopedEventLogs.slice(0, 100));
          // "오늘 이벤트" 카운트는 표시용 100칸 슬라이스가 아니라 전체(잘리기 전)에서 센다.
          // 카메라 on/off 이벤트가 100칸을 잠식해 오늘 PPE가 밀려나 숫자가 줄어들던 버그 방지.
          const _todayStr = new Intl.DateTimeFormat("en-CA", {
            timeZone: "Asia/Seoul",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }).format(new Date());
          const _ROUTINE = new Set(["MINTCAM_ON", "MINTCAM_OFF"]);
          const _cnt = scopedEventLogs.filter((r) => {
            const t = String(r?.timestamp || r?.createdAt || r?.displayTime || "");
            return (
              t.startsWith(_todayStr) &&
              !_ROUTINE.has(String(r?.eventType || "").toUpperCase())
            );
          }).length;
          setTodayEventCount(_cnt);
        } else {
          setLogs([]);
          setTodayEventCount(0);
        }

        let activeDangerItems = [];

        if (includeDangerSummary && responses[1]) {
          try {
            const summaryData = await responses[1].json();
            const rawActive = Array.isArray(summaryData?.activeEvents)
              ? summaryData.activeEvents
              : [];

            activeDangerItems = rawActive.map((item) =>
              enrichLog({
                ...item,
                type:
                  item?.eventType === "HEART_RATE_HIGH"
                    ? "심박수 이상"
                    : item?.type || "위험 감지",
                message: item?.message || item?.reason || "활성 위험 감지",
              })
            );

            activeDangerItems = filterLogsByAccountScope(
              activeDangerItems,
              currentUser,
              scopedCameras
            );
          } catch {
            activeDangerItems = [];
          }
        }

        setActiveDangerLogs(activeDangerItems);

        if (!allowBell) {
          setRecentAlertLogs([]);
          setUnreadAlerts(0);
          setToastItem(null);
          return;
        }

        const mergedBellSource = [...activeDangerItems, ...scopedEventLogs];

        const uniqueMap = new Map();
        for (const item of mergedBellSource) {
          if (!item?._key) continue;
          if (!isBellTarget(item)) continue;

          const existing = uniqueMap.get(item._key);
          if (!existing || Number(item._ms || 0) > Number(existing._ms || 0)) {
            uniqueMap.set(item._key, item);
          }
        }

        const bellLogs = Array.from(uniqueMap.values()).sort(
          (a, b) => Number(b?._ms || 0) - Number(a?._ms || 0)
        );

        // 서버 읽음 기준시각을 로컬 cutoff와 병합(더 큰 값=더 많이 읽음 우선).
        // → 다른 기기에서 읽음처리한 게 이 기기에도 반영된다.
        try {
          const readState = await readStatePromise;
          const serverCutoff = Number(readState?.cutoffMs || 0);
          if (
            Number.isFinite(serverCutoff) &&
            serverCutoff > Number(readAlertCutoffRef.current || 0)
          ) {
            readAlertCutoffRef.current = serverCutoff;
            saveReadAlertState(readAlertKeysRef.current, serverCutoff);
          }
        } catch {}

        const unreadOnly = bellLogs.filter((item) => !isReadAlert(item));

        setRecentAlertLogs(unreadOnly.slice(0, 10));
        setUnreadAlerts(unreadOnly.length);

        if (!eventLogBootstrappedRef.current) {
          unreadOnly.forEach((item) => {
            if (item?._key) {
              seenAlertKeysRef.current.add(item._key);
            }
          });
          eventLogBootstrappedRef.current = true;
          return;
        }

        const newItems = unreadOnly.filter(
          (item) => item?._key && !seenAlertKeysRef.current.has(item._key)
        );

        if (newItems.length > 0) {
          newItems.forEach((item) => seenAlertKeysRef.current.add(item._key));
          setToastItem(newItems[0]);

          if (toastTimerRef.current) {
            clearTimeout(toastTimerRef.current);
          }

          toastTimerRef.current = setTimeout(() => {
            setToastItem(null);
          }, 4500);
        }
      } catch {
        setLogs([]);
        setRecentAlertLogs([]);
        setUnreadAlerts(0);
        setToastItem(null);
        setActiveDangerLogs([]);
      } finally {
        fetchInFlightRef.current = false;
        scheduleNextFetch(getNextPollMs());
      }
    };

    const handleVisibilityChange = () => {
      if (cancelled) return;

      if (typeof document !== "undefined" && !document.hidden) {
        fetchLogs();
      } else {
        scheduleNextFetch(getNextPollMs());
      }
    };

    fetchLogs();

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      cancelled = true;
      fetchInFlightRef.current = false;

      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }

      clearPollTimer();

      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = null;
      }
    };
  }, [
    apiBase,
    enabled,
    allowBell,
    allowEventLogs,
    alertOpen,
    includeDangerSummary,
    currentUser?.id,
    currentUser?.loginId,
    currentUser?.roleCode,
    scopedCameraKey,
  ]);

  const handleClearAlerts = () => {
    const nextReadKeys = new Set(readAlertKeysRef.current);
    let nextCutoff = Number(readAlertCutoffRef.current || 0);

    recentAlertLogs.forEach((item) => {
      if (item?._key) {
        nextReadKeys.add(item._key);
        seenAlertKeysRef.current.add(item._key);
      }
      if (item?._ms && item._ms > nextCutoff) {
        nextCutoff = item._ms;
      }
    });

    readAlertKeysRef.current = nextReadKeys;
    readAlertCutoffRef.current = nextCutoff;
    saveReadAlertState(nextReadKeys, nextCutoff);

    // 서버에도 읽음 기준시각을 저장 → 다른 기기/브라우저와 공유(다기기 동기화).
    try {
      fetch(`${apiBase}/api/alert-read-state`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ cutoffMs: Number(nextCutoff) || 0 }),
      }).catch(() => {});
    } catch {}

    setRecentAlertLogs([]);
    setUnreadAlerts(0);
    setToastItem(null);
    setAlertOpen(false);
  };

  return {
    alertOpen,
    setAlertOpen,
    unreadAlerts,
    toastItem,
    setToastItem,
    recentAlertLogs,
    activeDangerLogs,
    logs,
    todayEventCount,
    handleClearAlerts,
  };
}